/**
 * Trial countdown banner shown on the Dashboard while the tenant is on the
 * business trial. Colour + urgency escalate as days_left decreases:
 *   > 7     — soft green info
 *   4–7     — amber warning
 *   ≤ 3     — red critical
 * The user can dismiss it once via the `x`, stored in localStorage so it
 * reappears on the next login or when we cross an urgency threshold.
 */
import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, AlertTriangle, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TrialBanner({ user, lang = "es" }) {
  const daysLeft = user?.trial_days_left ?? 0;
  const isTrialing = !!user?.is_trialing;
  const tier = daysLeft <= 3 ? "critical" : daysLeft <= 7 ? "warn" : "info";
  const key = `trial-banner-dismissed:${user?.id}:${tier}`;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    try { setDismissed(!!localStorage.getItem(key)); } catch { /* noop */ }
  }, [key]);

  const styles = useMemo(() => ({
    critical: {
      bg: "bg-gradient-to-r from-red-50 via-red-100/60 to-red-50",
      border: "border-red-300",
      badge: "bg-red-600 text-white",
      title: "text-red-900",
      icon: AlertTriangle,
    },
    warn: {
      bg: "bg-gradient-to-r from-amber-50 via-amber-100/50 to-amber-50",
      border: "border-amber-300",
      badge: "bg-amber-600 text-white",
      title: "text-amber-900",
      icon: AlertTriangle,
    },
    info: {
      bg: "bg-gradient-to-r from-emerald-50 via-emerald-100/50 to-emerald-50",
      border: "border-emerald-300",
      badge: "bg-emerald-600 text-white",
      title: "text-emerald-900",
      icon: Sparkles,
    },
  }[tier]), [tier]);

  if (!isTrialing || dismissed) return null;

  const Icon = styles.icon;
  const headline = lang === "es"
    ? (tier === "critical"
        ? (daysLeft <= 1 ? "Tu prueba acaba mañana" : `Solo quedan ${daysLeft} días de prueba`)
        : tier === "warn"
          ? `Última semana de prueba · ${daysLeft} días`
          : `Prueba Business activa · ${daysLeft} días`)
    : (tier === "critical"
        ? (daysLeft <= 1 ? "Your trial ends tomorrow" : `Only ${daysLeft} days of trial left`)
        : tier === "warn"
          ? `Last week of trial · ${daysLeft} days`
          : `Business trial · ${daysLeft} days`);
  const subline = lang === "es"
    ? "Al terminar, tu cuenta pasa a Free (200 docs). Perderás Tesorería, Deudas, Inventario, Carta y Presupuesto."
    : "When it ends, your account moves to Free (200 docs). You'll lose Treasury, Debts, Inventory, Menu and Budget.";

  const dismiss = () => {
    try { localStorage.setItem(key, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className={`relative rounded-xl border-2 ${styles.border} ${styles.bg} px-5 py-4 shadow-sm`}
      data-testid={`trial-banner-${tier}`}
      role="alert"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Icon size={22} strokeWidth={2} className={styles.title} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${styles.badge}`}>
                {lang === "es" ? "Prueba" : "Trial"}
              </span>
              <h3 className={`text-base font-semibold ${styles.title}`}>{headline}</h3>
            </div>
            <p className="text-[13px] text-[var(--text-secondary)] mt-1 leading-snug">{subline}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/settings" data-testid="trial-banner-cta">
            <Button
              size="sm"
              className={`h-9 px-4 text-xs font-semibold ${tier === "critical" ? "bg-red-600 hover:bg-red-700" : tier === "warn" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}
            >
              {lang === "es" ? "Ver planes" : "See plans"}
              <ArrowRight size={13} className="ml-1.5" />
            </Button>
          </Link>
          <button
            onClick={dismiss}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] p-1"
            title={lang === "es" ? "Ocultar hasta próxima etapa" : "Dismiss until next stage"}
            data-testid="trial-banner-dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
